import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { Add, Block, EventSeat, NotificationsActive, Print } from '@mui/icons-material';
import Navbar from '../setup/Navbar';
import { fetchDineInTables } from '../../features/setup/dineInTableSlice';
import {
    fetchDineInWaitlist,
    createDineInWaitlistToken,
    callDineInWaitlistToken,
    seatDineInWaitlistToken,
    cancelDineInWaitlistToken,
    printDineInWaitlistToken,
    reset as resetWaitlist
} from '../../features/setup/dineInWaitlistSlice';
import '../../services/GlobalVariables';
import {
    broadcastWaitlistAnnouncement,
    buildWaitlistAnnouncementPayload,
    normalizeQueueDate
} from '../../utils/waitlistAnnouncements';

const formatDateTime = (value) => {
    if (!value) return '-';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';

    return date.toLocaleString('en-PK');
};

const formatTokenNumber = (tokenNo) => String(tokenNo || 0).padStart(3, '0');

const formatQueueDateLabel = (value) => {
    if (!value) return '-';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    return date.toLocaleDateString('en-PK');
};

const getWaitlistStatusColor = (status) => {
    if (status === 'waiting') return 'info';
    if (status === 'called') return 'warning';
    if (status === 'seated') return 'success';
    if (status === 'cancelled') return 'default';
    return 'default';
};

export default function DineInTokenManagement() {
    const dispatch = useDispatch();
    const { tables, isLoading: isTableLoading } = useSelector((state) => state.dineInTables);
    const {
        waitlist,
        isLoading: isWaitlistLoading,
        isError: isWaitlistError,
        isSuccess: isWaitlistSuccess,
        message: waitlistMessage
    } = useSelector((state) => state.dineInWaitlist);
    const { user } = useSelector((state) => state.auth);

    const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
    const [seatDialogOpen, setSeatDialogOpen] = useState(false);
    const [selectedWaitToken, setSelectedWaitToken] = useState(null);
    const [assignedTableLabel, setAssignedTableLabel] = useState('');
    const [testingPrinter, setTestingPrinter] = useState(false);
    const [printerConfig, setPrinterConfig] = useState({
        printerIP: localStorage.getItem('dineInTokenPrinterIP') || '',
        printerPort: Number.parseInt(localStorage.getItem('dineInTokenPrinterPort') || '9100', 10) || 9100
    });
    const [bookingForm, setBookingForm] = useState({
        customerName: '',
        customerPhone: '',
        personsCount: '2',
        preferredTableLabel: '',
        notes: ''
    });

    useEffect(() => {
        dispatch(fetchDineInTables());
        dispatch(fetchDineInWaitlist());
    }, [dispatch]);

    useEffect(() => {
        if (isWaitlistSuccess && waitlistMessage) {
            alert(waitlistMessage);
            dispatch(resetWaitlist());
            setBookingDialogOpen(false);
            setSeatDialogOpen(false);
            setSelectedWaitToken(null);
            setAssignedTableLabel('');
            setBookingForm({ customerName: '', customerPhone: '', personsCount: '2', preferredTableLabel: '', notes: '' });
            dispatch(fetchDineInWaitlist());
        }

        if (isWaitlistError && waitlistMessage) {
            alert(waitlistMessage);
            dispatch(resetWaitlist());
        }
    }, [isWaitlistSuccess, isWaitlistError, waitlistMessage, dispatch]);

    const waitlistSummary = useMemo(() => ({
        active: waitlist.filter((item) => ['waiting', 'called'].includes(item.status)).length,
        waiting: waitlist.filter((item) => item.status === 'waiting').length,
        called: waitlist.filter((item) => item.status === 'called').length,
        seatedToday: waitlist.filter((item) => item.status === 'seated' && normalizeQueueDate(item.queueDate) === normalizeQueueDate(new Date())).length
    }), [waitlist]);

    const freeTables = useMemo(
        () => tables.filter((table) => table.status === 'free'),
        [tables]
    );

    const getSuitableTables = (personsCount) => (
        freeTables.filter((table) => Number(table.seatingCapacity || 0) >= Number(personsCount || 0))
    );

    const getQueuePosition = (item) => {
        if (!['waiting', 'called'].includes(item.status)) {
            return '-';
        }

        return waitlist.filter((entry) => (
            entry.queueDate === item.queueDate
            && ['waiting', 'called'].includes(entry.status)
            && Number(entry.tokenNo || 0) <= Number(item.tokenNo || 0)
        )).length;
    };

    const handleBookingInputChange = (field) => (event) => {
        setBookingForm((prev) => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    const savePrinterConfig = () => {
        const printerIP = String(printerConfig.printerIP || '').trim();
        const printerPort = Number.parseInt(String(printerConfig.printerPort || 9100), 10) || 9100;

        if (!printerIP) {
            alert('Printer IP required hai');
            return;
        }

        localStorage.setItem('dineInTokenPrinterIP', printerIP);
        localStorage.setItem('dineInTokenPrinterPort', String(printerPort));
        setPrinterConfig({ printerIP, printerPort });
        alert(`Token printer saved: ${printerIP}:${printerPort}`);
    };

    const testTokenPrinter = async () => {
        const printerIP = String(printerConfig.printerIP || '').trim();
        const printerPort = Number.parseInt(String(printerConfig.printerPort || 9100), 10) || 9100;

        if (!printerIP) {
            alert('Printer IP required hai');
            return;
        }

        setTestingPrinter(true);
        try {
            await axios.post(global.SET_API_URL + 'printerAssignment/testPrinter', {
                printerIP,
                printerPort,
                categoryDesciption: 'Dine-In Token Printer'
            });
            alert('Printer test successful');
        } catch (error) {
            alert(`Printer test failed: ${error.response?.data?.message || error.message}`);
        } finally {
            setTestingPrinter(false);
        }
    };

    const printTokenSlip = async (item) => {
        if (!item?.waitlistID) {
            return;
        }

        const printerIP = String(printerConfig.printerIP || '').trim();
        const printerPort = Number.parseInt(String(printerConfig.printerPort || 9100), 10) || 9100;

        if (!printerIP) {
            alert('Pehle printer IP set karein');
            return;
        }

        try {
            await dispatch(printDineInWaitlistToken({
                waitlistID: item.waitlistID,
                printerIP,
                printerPort
            })).unwrap();
        } catch (error) {
            // Error handled by slice state.
        }
    };

    const handleCreateBooking = async (shouldPrint = false) => {
        const customerName = String(bookingForm.customerName || '').trim();
        const personsCount = Number.parseInt(String(bookingForm.personsCount || ''), 10);

        if (!customerName) {
            alert('Customer name is required');
            return;
        }

        if (!Number.isInteger(personsCount) || personsCount < 1 || personsCount > 50) {
            alert('Persons count must be between 1 and 50');
            return;
        }

        const payload = {
            customerName,
            customerPhone: bookingForm.customerPhone,
            personsCount,
            preferredTableLabel: bookingForm.preferredTableLabel || null,
            notes: bookingForm.notes,
            userID: user?.userID || null
        };

        try {
            const response = await dispatch(createDineInWaitlistToken(payload)).unwrap();

            if (shouldPrint) {
                await printTokenSlip({
                    waitlistID: response.waitlistID,
                    tokenNo: response.tokenNo,
                    queueDate: response.queueDate,
                    customerName,
                    customerPhone: payload.customerPhone,
                    personsCount,
                    preferredTableLabel: payload.preferredTableLabel,
                    notes: payload.notes,
                    createdAt: new Date()
                });
            }
        } catch (error) {
            // Error handling is managed by slice state.
        }
    };

    const handleCallToken = async (item) => {
        try {
            await dispatch(callDineInWaitlistToken(item.waitlistID)).unwrap();
            broadcastWaitlistAnnouncement(buildWaitlistAnnouncementPayload(item));
        } catch (error) {
            // Error handling is managed by slice state.
        }
    };

    const openSeatDialog = (item) => {
        const matchingTables = getSuitableTables(item.personsCount);
        const preferredMatchingTable = matchingTables.find((table) => table.tableLabel === item.preferredTableLabel);
        setSelectedWaitToken(item);
        setAssignedTableLabel(item.assignedTableLabel || preferredMatchingTable?.tableLabel || matchingTables[0]?.tableLabel || '');
        setSeatDialogOpen(true);
    };

    const handleSeatToken = () => {
        if (!selectedWaitToken) {
            return;
        }

        dispatch(seatDineInWaitlistToken({
            waitlistID: selectedWaitToken.waitlistID,
            data: {
                assignedTableLabel: assignedTableLabel || null
            }
        }));
    };

    const handleCancelToken = (item) => {
        if (!window.confirm(`Cancel token ${formatTokenNumber(item.tokenNo)} for ${item.customerName}?`)) {
            return;
        }

        dispatch(cancelDineInWaitlistToken(item.waitlistID));
    };

    const closeBookingDialog = () => {
        setBookingDialogOpen(false);
        setBookingForm({ customerName: '', customerPhone: '', personsCount: '2', preferredTableLabel: '', notes: '' });
    };

    const closeSeatDialog = () => {
        setSeatDialogOpen(false);
        setSelectedWaitToken(null);
        setAssignedTableLabel('');
    };

    return (
        <>
            <Navbar />
            <Box sx={{ p: 2 }}>
                {(isTableLoading || isWaitlistLoading) && <LinearProgress />}

                <Card>
                    <CardContent>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ xs: 'stretch', md: 'center' }}>
                            <TextField
                                label="Token Printer IP"
                                value={printerConfig.printerIP}
                                onChange={(event) => setPrinterConfig((prev) => ({ ...prev, printerIP: event.target.value }))}
                                size="small"
                                sx={{ minWidth: 220 }}
                                placeholder="192.168.1.200"
                            />
                            <TextField
                                label="Port"
                                type="number"
                                value={printerConfig.printerPort}
                                onChange={(event) => setPrinterConfig((prev) => ({ ...prev, printerPort: Number(event.target.value) || 9100 }))}
                                size="small"
                                sx={{ width: 120 }}
                            />
                            <Button variant="outlined" onClick={savePrinterConfig}>Save Printer</Button>
                            <Button variant="outlined" onClick={testTokenPrinter} disabled={testingPrinter}>
                                {testingPrinter ? 'Testing...' : 'Test Printer'}
                            </Button>
                        </Stack>

                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                            <Box>
                                <Typography variant="h5" fontWeight="bold">Dine-In Token Management</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Every active opening date starts numbering from 001.
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Chip label={`Active: ${waitlistSummary.active}`} color="primary" variant="outlined" />
                                <Chip label={`Waiting: ${waitlistSummary.waiting}`} color="info" variant="outlined" />
                                <Chip label={`Called: ${waitlistSummary.called}`} color="warning" variant="outlined" />
                                <Chip label={`Seated Today: ${waitlistSummary.seatedToday}`} color="success" variant="outlined" />
                                <Button startIcon={<Add />} variant="contained" onClick={() => setBookingDialogOpen(true)}>Add Token</Button>
                            </Stack>
                        </Stack>

                        <Alert severity="info" sx={{ mb: 2 }}>
                            Agar daily opening date change ho jaye to nayi date par token numbering automatically 1 se restart ho jayegi.
                        </Alert>

                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Token</strong></TableCell>
                                        <TableCell><strong>Queue Date</strong></TableCell>
                                        <TableCell><strong>Position</strong></TableCell>
                                        <TableCell><strong>Customer</strong></TableCell>
                                        <TableCell><strong>Phone</strong></TableCell>
                                        <TableCell><strong>Persons</strong></TableCell>
                                        <TableCell><strong>Preferred Table</strong></TableCell>
                                        <TableCell><strong>Suitable Free Tables</strong></TableCell>
                                        <TableCell><strong>Status</strong></TableCell>
                                        <TableCell><strong>Assigned Table</strong></TableCell>
                                        <TableCell><strong>Created At</strong></TableCell>
                                        <TableCell align="center"><strong>Actions</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {waitlist.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={12} align="center">No advance booking tokens found.</TableCell>
                                        </TableRow>
                                    )}
                                    {waitlist.map((item) => {
                                        const suitableTables = getSuitableTables(item.personsCount);
                                        const suitableText = suitableTables.length === 0
                                            ? 'No free table'
                                            : suitableTables.slice(0, 4).map((table) => table.tableLabel).join(', ');
                                        const canCall = item.status === 'waiting';
                                        const canSeat = ['waiting', 'called'].includes(item.status);
                                        const canCancel = ['waiting', 'called'].includes(item.status);

                                        return (
                                            <TableRow key={item.waitlistID} hover>
                                                <TableCell>#{formatTokenNumber(item.tokenNo)}</TableCell>
                                                <TableCell>{formatQueueDateLabel(item.queueDate)}</TableCell>
                                                <TableCell>{getQueuePosition(item)}</TableCell>
                                                <TableCell>{item.customerName}</TableCell>
                                                <TableCell>{item.customerPhone || '-'}</TableCell>
                                                <TableCell>{Number(item.personsCount || 0)}</TableCell>
                                                <TableCell>{item.preferredTableLabel || '-'}</TableCell>
                                                <TableCell>{suitableText}</TableCell>
                                                <TableCell>
                                                    <Chip size="small" label={item.status} color={getWaitlistStatusColor(item.status)} />
                                                </TableCell>
                                                <TableCell>{item.assignedTableLabel || '-'}</TableCell>
                                                <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title={canCall ? 'Call token' : 'Only waiting tokens can be called'}>
                                                        <span>
                                                            <IconButton color="warning" size="small" disabled={!canCall} onClick={() => handleCallToken(item)}>
                                                                <NotificationsActive />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title={canSeat ? 'Mark token seated' : 'Only active tokens can be seated'}>
                                                        <span>
                                                            <IconButton color="success" size="small" disabled={!canSeat} onClick={() => openSeatDialog(item)}>
                                                                <EventSeat />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title={canCancel ? 'Cancel token' : 'Only active tokens can be cancelled'}>
                                                        <span>
                                                            <IconButton color="error" size="small" disabled={!canCancel} onClick={() => handleCancelToken(item)}>
                                                                <Block />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Print token slip">
                                                        <span>
                                                            <IconButton color="primary" size="small" onClick={() => printTokenSlip(item)}>
                                                                <Print />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>

                <Dialog open={bookingDialogOpen} onClose={closeBookingDialog} fullWidth maxWidth="sm">
                    <DialogTitle>Advance Booking Token</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            fullWidth
                            margin="dense"
                            label="Customer Name"
                            value={bookingForm.customerName}
                            onChange={handleBookingInputChange('customerName')}
                        />
                        <TextField
                            fullWidth
                            margin="dense"
                            label="Phone"
                            value={bookingForm.customerPhone}
                            onChange={handleBookingInputChange('customerPhone')}
                        />
                        <TextField
                            fullWidth
                            margin="dense"
                            type="number"
                            label="Persons Count"
                            value={bookingForm.personsCount}
                            onChange={handleBookingInputChange('personsCount')}
                            inputProps={{ min: 1, max: 50 }}
                            helperText="Queue token persons wise generate hoga"
                        />
                        <TextField
                            select
                            fullWidth
                            margin="dense"
                            label="Table No (Optional)"
                            value={bookingForm.preferredTableLabel}
                            onChange={handleBookingInputChange('preferredTableLabel')}
                            helperText="Token allot karte waqt preferred table select kar sakte hain"
                        >
                            <MenuItem value="">No preference</MenuItem>
                            {tables.map((table) => (
                                <MenuItem key={table.reservationID} value={table.tableLabel}>
                                    {table.tableLabel} ({table.seatingCapacity} persons)
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            fullWidth
                            margin="dense"
                            label="Notes"
                            value={bookingForm.notes}
                            onChange={handleBookingInputChange('notes')}
                            multiline
                            minRows={2}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeBookingDialog}>Cancel</Button>
                        <Button onClick={() => handleCreateBooking(true)} variant="outlined">Create & Print</Button>
                        <Button onClick={() => handleCreateBooking(false)} variant="contained">Create Token</Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={seatDialogOpen} onClose={closeSeatDialog} fullWidth maxWidth="sm">
                    <DialogTitle>Mark Token Seated</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            {selectedWaitToken
                                ? `Token #${formatTokenNumber(selectedWaitToken.tokenNo)} | ${selectedWaitToken.customerName} | ${selectedWaitToken.personsCount} persons`
                                : ''}
                        </Typography>
                        <TextField
                            select
                            fullWidth
                            margin="dense"
                            label="Assign Table (Optional)"
                            value={assignedTableLabel}
                            onChange={(event) => setAssignedTableLabel(event.target.value)}
                            helperText="Sirf free aur matching capacity wali tables list me dikh rahi hain"
                        >
                            <MenuItem value="">No table selected</MenuItem>
                            {selectedWaitToken && getSuitableTables(selectedWaitToken.personsCount).map((table) => (
                                <MenuItem key={table.reservationID} value={table.tableLabel}>
                                    {table.tableLabel} ({table.seatingCapacity} persons)
                                </MenuItem>
                            ))}
                        </TextField>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeSeatDialog}>Cancel</Button>
                        <Button onClick={handleSeatToken} variant="contained" color="success">Mark Seated</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </>
    );
}
